/* eslint-disable complexity */
/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { skipToken } from '@reduxjs/toolkit/query';
import { FMH_NPI, FMH_UNMANAGED_NPI, NetworkStatuses } from '@strata/constants';
import { capitalizeString, formatAddress, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { Badge, FillProgressIcon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ConvertBiosimilarDialog } from 'components/referrals/dialogs/ConvertBiosimilarDialog';
import { ConvertBiosimilarDialogLegacy } from 'components/referrals/dialogs/ConvertBiosimilarDialogLegacy';
import { CreateWorklistTaskDialog } from 'components/referrals/dialogs/CreateWorklistTaskDialog';
import { SendEReferralDialog } from 'components/referrals/dialogs/SendEReferralDialog';
import { SendFaxDialog } from 'components/referrals/dialogs/SendFaxDialog';
import { SubmitReferralDialog } from 'components/referrals/dialogs/SubmitReferralDialog';
import { UpdateReferralDataDialog } from 'components/referrals/dialogs/UpdateReferralDataDialog';
import { UpdateToLiveReferralDialog } from 'components/referrals/dialogs/UpdateToLiveReferralDialog';
import { Messages } from 'components/referrals/Messages';
import { PerformanceProgress } from 'components/referrals/PerformanceProgress';
import { PharmacyActivity } from 'components/referrals/PharmacyActivity';
import { ReferralAndOnboardingStatusIndicator } from 'components/referrals/ReferralAndOnboardingStatusIndicator';
import { ReferralChecklist } from 'components/referrals/ReferralChecklist';
import { ReferralDetailsOverview } from 'components/referrals/ReferralDetailsOverview';
import { getReferralInlineMessages } from 'components/referrals/ReferralInlineMessages';
import { AuthDetails } from 'components/shared/AuthDetails';
import { LogFillActivityDialog } from 'components/shared/dialogs/LogFillActivityDialog';
import { FillActivity } from 'components/shared/FillActivity';
import { FmhAiChat } from 'components/shared/FmhAiChat';
import { Notes } from 'components/shared/Notes';
import { PrintHeader } from 'components/shared/PrintHeader';
import { SpecialMemberHandlingName } from 'components/shared/SpecialMemberHandlingName';
import { TherapyTransitionInlineMessages } from 'components/treatments/TherapyTransitionInlineMessages';
import { GRANDFATHERED_REFERRAL_CONTEXT_BANNER } from 'constants/grandfatheredTreatment';
import { useUserSupportConversations } from 'services/APIHooks';
import { UNSPECIFIED_NPI } from 'services/constants';
import { useShowEReferralStatus } from 'services/eReferralHelpers';
import { handleError } from 'services/Error';
import { isReferralRowDisabled } from 'services/gridHelpers';
import { buildIntakeCaseMilestoneReferralFieldsFromTreatment } from 'services/intakeCaseHelpers';
import { useEnrichedReferralMilestones } from 'services/milestoneHelpers';
import { useGoBack } from 'services/navigationHelpers';
import { useNeedsByDate } from 'services/needsByDateHelpers';
import { AppRoutes } from 'services/routes';
import {
    BIOSIMILAR_CONVERSION_IN_PROGRESS_MESSAGE,
    isBiosimilarConversionInProgress,
    shouldShowTherapyTransitionMessage,
    TransitionViewContext,
} from 'services/treatmentTransitionHelpers';
import { ConversationActions, MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { useSpecialMemberHandling } from 'services/useSpecialMemberHandling';
import { getAddableValueCases } from 'services/valueCaseHelpers';
import { useGetPrescriberFaxPrefQuery } from 'store/api/clientApi';
import { useGetObjectiveStatusesQuery } from 'store/api/onboardingApi';
import {
    useGetReferralSupportCapabilityQuery,
    useGetValueCasesQuery,
} from 'store/api/performanceApi';
import {
    useGetAllProductServicesQuery,
    useGetBiosimilarConversionProductsQuery,
    useGetPayerSegmentsQuery,
    useGetPrescriberDetailsQuery,
    useGetProductByOidQuery,
    useGetSupplierDetailsQuery,
} from 'store/api/referenceApi';
import {
    useGetReferralTransitionInfoQuery,
    useGetTherapyGroupContainmentConfigQuery,
    useGetTreatmentDetailsQuery,
} from 'store/api/treatmentApi';
import { useGetOnboardingActivityQuery } from 'store/api/userSupportApi';
import {
    fetchPerformanceEntries,
    fetchReferralDetails,
    fetchReferralEvents,
} from 'store/referrals/referralsSlice';

/** Agora Action Dialogs Enum */
const ActionDialogs = {
    CONVERT_BIOSIMILAR: 'CONVERT_BIOSIMILAR',
    CREATE_WORKLIST_TASK: 'CREATE_WORKLIST_TASK',
    SEND_FAX: 'SEND_FAX',
    SUBMIT_REFERRAL: 'SUBMIT_REFERRAL',
    UPDATE_REFERRAL_DATA: 'UPDATE_REFERRAL_DATA',
    UPDATE_TO_LIVE_REFERRAL: 'UPDATE_TO_LIVE_REFERRAL',
    LOG_FILL_ACTIVITY: 'LOG_FILL_ACTIVITY',
    SEND_E_REFERRAL: 'SEND_E_REFERRAL',
};

const ActionDialogComponentMap = {
    [ActionDialogs.CREATE_WORKLIST_TASK]: CreateWorklistTaskDialog,
    [ActionDialogs.SEND_FAX]: SendFaxDialog,
    [ActionDialogs.SUBMIT_REFERRAL]: SubmitReferralDialog,
    [ActionDialogs.UPDATE_REFERRAL_DATA]: UpdateReferralDataDialog,
    [ActionDialogs.UPDATE_TO_LIVE_REFERRAL]: UpdateToLiveReferralDialog,
    [ActionDialogs.LOG_FILL_ACTIVITY]: LogFillActivityDialog,
    [ActionDialogs.SEND_E_REFERRAL]: SendEReferralDialog,
};

const backToDestinationDefault = {
    route: AppRoutes.referralsList,
    text: 'Referrals',
};

const isEligibleReferralForBiosimilarConversion = (treatmentDetails, referralDetails) => {
    if (!treatmentDetails || !referralDetails) {
        return false;
    }

    const isActiveReferralOnTreatment =
        treatmentDetails.isActive &&
        treatmentDetails.activeReferral?.referralId === referralDetails.referralId &&
        referralDetails.referralStatus === ReferralStatusIndicator.Statuses.PHARM &&
        !!referralDetails.supplierNpi;

    return isActiveReferralOnTreatment;
};

export const ReferralDetailPage = () => {
    const { agoraAiChat, showAddAdHocValueCaseAgora, tgh2ConversionDialogEnhancements } =
        useFlags();
    const actionDialogComponentMap = {
        ...ActionDialogComponentMap,
        [ActionDialogs.CONVERT_BIOSIMILAR]: tgh2ConversionDialogEnhancements
            ? ConvertBiosimilarDialog
            : ConvertBiosimilarDialogLegacy,
    };
    const params = useParams();
    const referralId = decodeURIComponent(params.referralId);
    const { payerId } = params;
    const { user } = useAuth0();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data: canSupplierViewReferral } = useGetReferralSupportCapabilityQuery(
        { referralId },
        {
            skip: !referralId,
        },
    );

    const { data } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = data ?? {};

    const { backToDestination, goBack } = useGoBack(backToDestinationDefault);

    const referralDetails = useSelector((state) => state.referrals.referralDetails[referralId]);
    const {
        data: treatmentDetails,
        isFetching: treatmentFetching,
        refetch: refetchTreatment,
    } = useGetTreatmentDetailsQuery(
        {
            payerId,
            treatmentId: referralDetails?.treatmentId,
        },
        {
            skip: !referralDetails,
        },
    );

    const intakeCaseMilestoneReferralFields = useMemo(
        () => buildIntakeCaseMilestoneReferralFieldsFromTreatment(treatmentDetails, referralId),
        [treatmentDetails, referralId],
    );

    useEffect(() => {
        datadogLogs.logger.info('intake_milestones_referral_detail_inputs', {
            referral_id: referralId,
            treatment_id: referralDetails?.treatmentId ?? '',
            treatment_fetching: String(treatmentFetching),
            intake_fields_present: String(intakeCaseMilestoneReferralFields != null),
            timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
        });
    }, [
        referralId,
        referralDetails?.treatmentId,
        treatmentFetching,
        intakeCaseMilestoneReferralFields,
    ]);

    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        { supplierNpi: referralDetails?.supplierNpi },
        { skip: !referralDetails?.supplierNpi },
    );
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        { prescriberNpi: referralDetails?.prescriberNpi },
        { skip: !referralDetails?.prescriberNpi },
    );
    const { data: prescriberFaxPref } = useGetPrescriberFaxPrefQuery(
        {
            payerId,
            segmentId: referralDetails?.segmentId,
        },
        { skip: !referralDetails },
    );
    const { data: productDetails } = useGetProductByOidQuery(referralDetails?.productServiceOid, {
        skip: !referralDetails?.productServiceOid,
    });
    const { data: therapyGroupContainmentConfig } = useGetTherapyGroupContainmentConfigQuery(
        { payerId, segmentId: referralDetails?.segmentId },
        { skip: !referralDetails?.segmentId },
    );
    const canShowConvertBiosimilar =
        therapyGroupContainmentConfig?.segmentTherapyGroupContainmentOn &&
        referralDetails?.productServiceOid &&
        isEligibleReferralForBiosimilarConversion(treatmentDetails, referralDetails);
    const { data: biosimilarConversionOptions } = useGetBiosimilarConversionProductsQuery(
        canShowConvertBiosimilar ? referralDetails.productServiceOid : skipToken,
    );
    const { data: events, isFetching: eventsFetching } = useGetOnboardingActivityQuery({
        referralId,
    });

    const { needsByDateData, showNeedsByDate } = useNeedsByDate({
        payerId,
        treatmentId: referralDetails?.treatmentId,
        referralId,
    });
    const { data: transitions } = useGetReferralTransitionInfoQuery(
        payerId && referralId ? { payerId, referralId } : skipToken,
    );
    const { data: allProductServicesData } = useGetAllProductServicesQuery();
    const showTherapyTransitionMessage = shouldShowTherapyTransitionMessage(
        transitions,
        allProductServicesData?.productServiceMap,
    );
    const conversionInProgress = isBiosimilarConversionInProgress(transitions);

    const performanceEntries = useSelector(
        (state) => state.referrals.performanceEntries[referralDetails?.referralId],
    );
    const { data: valueCases } = useGetValueCasesQuery(
        showAddAdHocValueCaseAgora ? undefined : skipToken,
    );
    const hasRemovablePerformanceRequirement = (performanceEntries ?? []).some(
        ({ status }) => status === 'not-started' || status === 'pending',
    );
    const hasAddableValueCase =
        showAddAdHocValueCaseAgora &&
        getAddableValueCases({ valueCases, referralDetails, performanceEntries }).length > 0;
    const { memberDetails } = useScopeAssessorMemberDetails(referralDetails);
    const specialMemberHandling = useSpecialMemberHandling(referralDetails, {
        routeCode: referralDetails?.routeCode,
        supplierNpi: referralDetails?.supplierNpi,
    });
    const { conversationList, referralConversationCounts } = useUserSupportConversations(
        referralDetails?.referralId,
    );
    const showFillProgress = [
        ReferralStatusIndicator.Statuses.PHARM,
        ReferralStatusIndicator.Statuses.PENDREAUTH,
    ].includes(referralDetails?.referralStatus);
    const { data: objectiveStatuses } = useGetObjectiveStatusesQuery(
        showFillProgress ? referralId : skipToken,
    );

    // Fetch Agora managed data for the referral
    useEffect(() => {
        datadogLogs.logger.info('referral_id changed - fetching new referral details', {
            referral_id: referralId,
            timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
        });
        dispatch(fetchReferralDetails({ payerId, referralId, notFoundCallback: goBack }));
    }, [dispatch, referralId, goBack, payerId]);

    // If referral details cannot be viewed, navigate back
    useEffect(() => {
        if (isReferralRowDisabled(referralDetails?.referralStatus)) {
            handleError('Details cannot be viewed for this referral.');
            goBack();
        }
    }, [referralDetails?.referralStatus, goBack]);

    // Use the Agora managed data to fetch referral details for the referral
    useEffect(() => {
        if (referralDetails?.referralId) {
            refetchTreatment();
            dispatch(fetchPerformanceEntries(referralDetails.referralId));
            dispatch(fetchReferralEvents(referralDetails.referralId));
        }
    }, [dispatch, refetchTreatment, referralDetails]);

    const { enrichedMilestones } = useEnrichedReferralMilestones({
        payerId,
        referralId,
        referralDetails,
    });

    const [actionDialogToShow, setActionDialogToShow] = useState();
    const ActiveActionDialog = actionDialogComponentMap[actionDialogToShow];
    const closeActionDialog = () => setActionDialogToShow();

    const [conversationDialogAction, setConversationDialogAction] = useState();

    const internalSuppliers = [FMH_NPI, FMH_UNMANAGED_NPI, UNSPECIFIED_NPI];
    const isReferralLite = referralDetails?.lite === true;
    const isSupplierActive = ![NetworkStatuses.LITE, NetworkStatuses.INACTIVE].includes(
        supplierDetails?.networkStatus,
    );
    const isReferralAtRealSupplier =
        referralDetails?.supplierNpi && !internalSuppliers.includes(referralDetails?.supplierNpi);
    const canShowCreateConversationAction = isReferralAtRealSupplier && canSupplierViewReferral;
    // Allow conversations to be viewed if any exist already
    // This covers the case where the referral was pending intake when the conversation was created by the pharmacy
    // We should not be able to create conversations in this state though
    const canShowMessagesTab =
        (canShowCreateConversationAction && isSupplierActive) || conversationList?.length > 0;

    const isReferralInvalid = referralDetails?.issues?.length > 0;

    const showEReferralStatus = useShowEReferralStatus(referralDetails, supplierDetails);

    // Order reflects order in dropdown menu, should be alphabetical
    // [ActionDialogs.*, condition, onItemClick?]
    const menuOptionsAndConditions = [
        {
            text: 'Create Support Conversation',
            condition: canShowCreateConversationAction,
            disabled: !isSupplierActive,
            onItemClick: () => {
                if ((location.hash ?? '').substring(1) !== MESSAGES_LOCATION_HASH) {
                    navigate({ hash: MESSAGES_LOCATION_HASH });
                }
                setConversationDialogAction(ConversationActions.CREATE);
            },
        },
        {
            type: ActionDialogs.CONVERT_BIOSIMILAR,
            condition:
                conversionInProgress ||
                (canShowConvertBiosimilar &&
                    referralDetails?.referralStatus !== ReferralStatusIndicator.Statuses.EXPIRED &&
                    !!referralDetails?.treatmentId &&
                    !referralDetails?.grandfathered &&
                    !isReferralLite &&
                    biosimilarConversionOptions?.length > 0),
            disabled: conversionInProgress,
            disabledReason: conversionInProgress
                ? BIOSIMILAR_CONVERSION_IN_PROGRESS_MESSAGE
                : undefined,
        },
        {
            type: ActionDialogs.CREATE_WORKLIST_TASK,
            condition: showNeedsByDate && needsByDateData?.needsByDate,
            disabled: isReferralLite,
        },
        {
            text: 'Go to Patient Treatment',
            condition: referralDetails?.treatmentId,
            onItemClick: () =>
                navigate(
                    AppRoutes.treatmentDetails(
                        referralDetails.payerId,
                        referralDetails.treatmentId,
                    ),
                    {
                        state: {
                            backToDestination: {
                                text: 'Referral Details',
                            },
                        },
                    },
                ),
        },
        {
            type: ActionDialogs.LOG_FILL_ACTIVITY,
            condition: referralDetails?.treatmentId,
        },
        {
            type: ActionDialogs.SEND_E_REFERRAL,
            condition:
                !isReferralInvalid &&
                [
                    ReferralStatusIndicator.Statuses.OUTREACH,
                    ReferralStatusIndicator.Statuses.PHARM,
                    ReferralStatusIndicator.Statuses.PENDREAUTH,
                ].includes(referralDetails?.referralStatus) &&
                showEReferralStatus,
        },
        {
            type: ActionDialogs.SEND_FAX,
            condition:
                !isReferralInvalid &&
                [
                    ReferralStatusIndicator.Statuses.OUTREACH,
                    ReferralStatusIndicator.Statuses.PHARM,
                    ReferralStatusIndicator.Statuses.PENDREAUTH,
                ].includes(referralDetails?.referralStatus),
        },
        {
            type: ActionDialogs.SUBMIT_REFERRAL,
            condition:
                !isReferralInvalid &&
                referralDetails?.referralStatus === ReferralStatusIndicator.Statuses.PENDAUTH,
        },
        {
            type: ActionDialogs.UPDATE_REFERRAL_DATA,
            condition:
                (isReferralInvalid ||
                    [
                        ReferralStatusIndicator.Statuses.FMH,
                        ReferralStatusIndicator.Statuses.PENDOUT,
                        ReferralStatusIndicator.Statuses.OUTREACH,
                        ReferralStatusIndicator.Statuses.PHARM,
                        ReferralStatusIndicator.Statuses.PENDREAUTH,
                        ReferralStatusIndicator.Statuses.RESEARCH,
                    ].includes(referralDetails?.referralStatus)) &&
                (hasRemovablePerformanceRequirement || hasAddableValueCase),
        },
        {
            type: ActionDialogs.UPDATE_TO_LIVE_REFERRAL,
            condition:
                !isReferralInvalid &&
                [
                    ReferralStatusIndicator.Statuses.FMH,
                    ReferralStatusIndicator.Statuses.RESEARCH,
                ].includes(referralDetails?.referralStatus),
        },
    ];

    const menuItems = menuOptionsAndConditions
        .filter(({ condition }) => condition)
        .map(({ type, text, disabled, disabledReason, onItemClick }) => ({
            disabled,
            disabledReason,
            text: type ? actionDialogComponentMap[type].Title : text,
            onItemClick: () => onItemClick?.() ?? setActionDialogToShow(type),
        }));

    return (
        <>
            {ActiveActionDialog && (
                <ActiveActionDialog
                    isOpen
                    onClose={closeActionDialog}
                    memberDetails={memberDetails}
                    needsByDateData={needsByDateData}
                    performanceEntries={performanceEntries}
                    prescriberDetails={prescriberDetails}
                    prescriberFaxPref={prescriberFaxPref}
                    referralDetails={referralDetails}
                    supplierDetails={supplierDetails}
                    username={user?.name}
                />
            )}
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title="Referral Details"
                            onClick={goBack}
                        />
                        <div
                            className="flex__gap--small"
                            style={{ marginLeft: 'auto', alignItems: 'center' }}
                        >
                            {agoraAiChat && <FmhAiChat />}
                            {menuItems.length > 0 && (
                                <ButtonWithDropdownMenu
                                    menuItems={menuItems}
                                    intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                    label="Actions"
                                />
                            )}
                        </div>
                    </>
                }
                rowTwoContentHeader={getReferralInlineMessages({
                    referralDetails,
                    enrichedMilestones,
                })}
                rowThreeContentHeader={
                    referralDetails && (
                        <>
                            <div className="print__header" style={{ display: 'none' }}>
                                <PrintHeader pageTitle="Authorized Referral" />
                            </div>
                            <FormTitleLabelValuePair
                                label={
                                    <SpecialMemberHandlingName
                                        name={formatNameLastFirst(
                                            referralDetails.patientLastName,
                                            referralDetails.patientFirstName,
                                        )}
                                        tooltip={specialMemberHandling?.tooltip}
                                    />
                                }
                                value={formatAddress(
                                    memberDetails?.memberCity,
                                    memberDetails?.memberState,
                                    memberDetails?.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Referral ID"
                                value={referralDetails?.friendlyId}
                            />
                            <HeaderLabelValuePair
                                label="Route Rule"
                                value={referralDetails?.routeName}
                            />
                            <HeaderLabelValuePair
                                label="Product"
                                value={productDetails?.description}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={payerSegmentMap?.[referralDetails?.segmentId]?.segment_name}
                            />
                            <HeaderLabelValuePair
                                label="Benefit"
                                value={capitalizeString(referralDetails?.benefit)}
                            />
                            <HeaderLabelValuePair
                                label="Referral Status"
                                value={
                                    <ReferralAndOnboardingStatusIndicator
                                        referralStatus={referralDetails?.referralStatus}
                                        referralId={referralDetails?.referralId}
                                        enrollmentStatus={memberDetails?.enrollmentStatus}
                                    />
                                }
                                showValueTooltip={false}
                            />
                            {showFillProgress && (
                                <HeaderLabelValuePair
                                    label="Fill Progress"
                                    value={
                                        <FillProgressIcon
                                            objectiveStatuses={objectiveStatuses ?? {}}
                                        />
                                    }
                                    showValueTooltip={false}
                                />
                            )}
                        </>
                    )
                }
                rowFourContentHeader={
                    (referralDetails?.grandfathered || showTherapyTransitionMessage) && (
                        <>
                            {showTherapyTransitionMessage && (
                                <TherapyTransitionInlineMessages
                                    payerId={payerId}
                                    referralId={referralId}
                                    backToDestination={{ text: 'Referral Details' }}
                                    viewContext={TransitionViewContext.REFERRAL}
                                />
                            )}
                            {referralDetails?.grandfathered && (
                                <InlineMessage
                                    intent={InlineMessage.Intent.INFO}
                                    wrapText
                                    label={GRANDFATHERED_REFERRAL_CONTEXT_BANNER}
                                />
                            )}
                        </>
                    )
                }
                activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                onTabSelect={(tab) =>
                    navigate(
                        {
                            ...location,
                            hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
                        },
                        { state: location.state, replace: true },
                    )
                }
                tabs={[
                    {
                        title: 'Overview',
                        mainContent: (
                            <ReferralDetailsOverview
                                payerId={payerId}
                                referralId={referralId}
                                showMemberDetails
                                showInlineMessages={false}
                                intakeCaseMilestoneReferralFields={
                                    intakeCaseMilestoneReferralFields
                                }
                            />
                        ),
                    },
                    {
                        title: 'Auth Details',
                        mainContent: (
                            <AuthDetails
                                auths={treatmentDetails?.authList}
                                isContingentTherapy={treatmentDetails?.isContingentTherapy}
                                isLoading={treatmentFetching}
                            />
                        ),
                    },
                    {
                        title: 'Suppliers',
                        mainContent: <PharmacyActivity referralDetails={referralDetails} />,
                    },
                    {
                        title: 'Performance',
                        mainContent:
                            performanceEntries?.length > 0 ? (
                                <PerformanceProgress performanceEntries={performanceEntries} />
                            ) : null,
                    },
                    ...(canShowMessagesTab
                        ? [
                              {
                                  title: 'Messages',
                                  className: 'tabbed-grid-content',
                                  mainContent: (
                                      <Messages
                                          referralId={referralDetails?.referralId}
                                          referralFriendlyId={referralDetails?.friendlyId}
                                          supplierNpi={referralDetails?.supplierNpi}
                                          segmentId={referralDetails?.segmentId}
                                          benefit={referralDetails?.benefit}
                                          memberFirstName={referralDetails?.patientFirstName}
                                          memberLastName={referralDetails?.patientLastName}
                                          conversations={conversationList}
                                          performanceEntries={performanceEntries}
                                          conversationDialogAction={conversationDialogAction}
                                          setConversationDialogAction={setConversationDialogAction}
                                      />
                                  ),
                                  badgeIntent: Badge.Intent.UNREAD,
                                  badgeCount: referralConversationCounts?.unread,
                              },
                          ]
                        : []),
                    {
                        title: 'Notes',
                        className: 'tabbed-grid-content',
                        mainContent: <Notes referralId={referralDetails?.referralId} />,
                    },
                    {
                        title: 'Fill Activity',
                        mainContent: <FillActivity events={events} isLoading={eventsFetching} />,
                    },
                    {
                        title: 'Raw Events',
                        mainContent: <ReferralChecklist referralId={referralDetails?.referralId} />,
                    },
                ]}
            />
        </>
    );
};
